import { makeStyles } from '@mui/styles';
import Table from 'apollo-react/components/Table';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { Confidence, ConfidenceLevel, ResultName } from './columns.data';
import { useFormSelector } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getUITA } from '../../utils/commonUtils';
import CustomLoader from '../CustomLoader';

const useStyles = makeStyles({
  content: {
    display: ({ isMobileDevice }) => (!isMobileDevice ? 'flex' : 'block'),
    justifyContent: 'space-around',
    height: 416,
    width: '100%',

    '& > div': {
      flex: 1,
    },
  },
  predictionTable: {
    width: '100%',
  },
  hide: {
    display: 'none',
  },
});

const columns = [
  {
    header: 'Term',
    accessor: 'Label',
    customCell: ResultName,
  },
  {
    header: 'IMDRF Code',
    accessor: 'Code',
  },
  {
    header: 'FDA Code',
    accessor: 'Id',
  },
  {
    header: 'Confidence %',
    accessor: 'Conf',
    customCell: Confidence,
  },
  {
    header: 'Confidence Level',
    accessor: 'Conf',
    customCell: ConfidenceLevel,
  },
];

const tablePaginationProps = (translateString) => ({
  // eslint-disable-next-line react/display-name
  labelDisplayedRows: ({ from, to, count }) => (
    <span style={{ whiteSpace: 'nowrap' }} ui-ta={getUITA('page-number')}>
      {`${from}-${to} ${translateString('of')} ${count}`}
    </span>
  ),
  labelRowsPerPage: translateString('Rows'),
  pageText: translateString('Page'),
  ofText: translateString('of'),
  nextButtonText: translateString('Next page'),
  lastButtonText: translateString('Last page'),
  backButtonText: translateString('Previous page'),
  firstButtonText: translateString('First page'),
  'ui-ta': getUITA('internal-pagination'),
  backButtonProps: {
    'ui-ta': getUITA('back'),
  },
  nextButtonProps: {
    'ui-ta': getUITA('next'),
  },
  SelectProps: {
    'ui-ta': getUITA('pagination-select'),
  },
  firstPageButtonProps: {
    'ui-ta': getUITA('first-page'),
  },
  lastPageButtonProps: {
    'ui-ta': getUITA('last-page'),
  },
  pageNumberInputProps: {
    'ui-ta': getUITA('pagination-select'),
  },
  dir: 'ltr',
});

const PredictionTable = ({
  type,
  searchBy,
  searchOptions,
  termHierarchy,
  regulatoryTerms,
  resetPagination,
  handleIsReverseTree,
  handleRepositoryItemClick,
  getDictionary,
  clearPredictions,
  setAllExpanded,
  changeSearchText,
  isMobileDevice,
}) => {
  const classes = useStyles({ isMobileDevice });
  const dispatch = useDispatch();
  const {
    loading,
    predictionResults,
    predictionLoading,
    hasPredictionRequestError,
  } = useFormSelector(({ regulatoryDictionaries }) => regulatoryDictionaries);
  const translateString = useTranslation();

  const predictionRows = predictionResults.Predictions1 ?? [];

  useEffect(() => {
    return () => {
      dispatch(clearPredictions());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchByPredictionText = (code, name) => {
    changeSearchText(name);
    resetPagination();
    handleIsReverseTree();
    handleRepositoryItemClick(null, null);

    dispatch(
      getDictionary(
        type,
        {
          HierarchyId: '',
          TextSearchOption: searchOptions,
          TermHierarchy: termHierarchy,
          Dictionary: regulatoryTerms,
          SearchText: code,
          SearchBy: searchBy,
          ShowAll: false,
        },
        true
      )
    );

    setAllExpanded(true);
  };

  return (
    <div className={classNames(!loading ? classes.content : classes.hide)}>
      {predictionLoading ? (
        <CustomLoader />
      ) : (
        !hasPredictionRequestError &&
        predictionResults.Predictions1?.length && (
          <Table
            className={classes.predictionTable}
            title={translateString('Device Problem Code Assistant')}
            columns={columns}
            rows={predictionRows.map((row) => ({
              ...row,
              handleSearchByPredictionText,
            }))}
            tablePaginationProps={tablePaginationProps(translateString)}
          />
        )
      )}
    </div>
  );
};

export default PredictionTable;
